import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Header from '../components/layout/Header';
import { 
  Trophy, ArrowLeft, Crown, Star, Medal, 
  Flame, Shield, Repeat, Award
} from 'lucide-react';
import axios from 'axios';
import { handleTwitchLogin } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Badge icon component
const BadgeIcon = ({ icon, color }) => {
  const colorClasses = {
    yellow: 'text-yellow-400',
    gold: 'text-amber-400',
    purple: 'text-purple-400',
    blue: 'text-blue-400',
    orange: 'text-orange-400',
    red: 'text-red-400'
  };
  
  const iconClass = `w-4 h-4 ${colorClasses[color] || 'text-gray-400'}`;
  
  switch (icon) {
    case 'crown': return <Crown className={iconClass} />;
    case 'trophy': return <Trophy className={iconClass} />;
    case 'star': return <Star className={iconClass} />;
    case 'repeat': return <Repeat className={iconClass} />;
    case 'flame': return <Flame className={iconClass} />;
    case 'shield': return <Shield className={iconClass} />;
    default: return <Award className={iconClass} />;
  }
};

// Rank badge component
const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return (
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center shadow-lg shadow-yellow-500/30">
        <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center shadow-lg shadow-gray-400/30">
        <Medal className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center shadow-lg shadow-amber-600/30">
        <Medal className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
      </div>
    );
  }
  return (
    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-red-900 to-red-950 flex items-center justify-center border border-red-500/30">
      <span className="text-base sm:text-lg font-bold text-red-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
        {rank}
      </span>
    </div>
  );
};

const Leaderboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await axios.get(`${API}/leaderboard`);
        setLeaderboard(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);

  const handleLogin = handleTwitchLogin;
  const safeLeaderboard = Array.isArray(leaderboard) ? leaderboard : [];

  return (
    <div className="min-h-screen bg-black">
      {/* Navigation */}
      <Header />

      {/* Main Content */}
      <main className="pt-20 sm:pt-24 pb-12 px-3 sm:px-4 max-w-4xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-red-500 to-red-600 mb-3 sm:mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <Trophy className="inline-block w-6 sm:w-10 h-6 sm:h-10 mr-2 sm:mr-3 text-yellow-400" />
            Leaderboard
          </h1>
          <p className="text-gray-400 text-lg">Top challengers ranked by wins, participation, and badges</p>
        </div>

        {/* Scoring Info */}
        <Card className="glass-card p-4 mb-8 border-red-900/30">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-lg">🥇</span>
              <span className="text-gray-400">1st = <span className="text-yellow-400 font-bold">100 pts</span></span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg">🥈</span>
              <span className="text-gray-400">2nd = <span className="text-gray-300 font-bold">50 pts</span></span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg">🥉</span>
              <span className="text-gray-400">3rd = <span className="text-amber-500 font-bold">25 pts</span></span>
            </div>
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-red-400" />
              <span className="text-gray-400">Play = <span className="text-red-400 font-bold">10 pts</span></span>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full"></div>
          </div>
        ) : safeLeaderboard.length === 0 ? (
          <Card className="glass-card p-12 text-center border-red-900/30">
            <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2 text-gray-400">No Rankings Yet</h3>
            <p className="text-gray-500 mb-6">Be the first to participate and climb the leaderboard!</p>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => navigate('/')}
            >
              Enter Challenge
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {safeLeaderboard.map((entry) => (
              <Card 
                key={entry.user_id} 
                className={`glass-card overflow-hidden transition-all duration-300 hover:scale-[1.02] ${
                  entry.rank === 1 ? 'border-yellow-500/50 shadow-lg shadow-yellow-500/20' :
                  entry.rank === 2 ? 'border-gray-400/50' :
                  entry.rank === 3 ? 'border-amber-600/50' :
                  user?.id === entry.user_id ? 'border-red-500/50' : 'border-red-900/30'
                }`}
                data-testid={`leaderboard-entry-${entry.rank}`}
              >
                <div className="p-4 sm:p-6">
                  <div className="flex items-center gap-4">
                    {/* Rank */}
                    <RankBadge rank={entry.rank} />
                    
                    {/* User Info */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <img 
                        src={entry.profile_image_url || 'https://via.placeholder.com/48'} 
                        alt={entry.display_name}
                        className={`w-12 h-12 rounded-full border-2 ${
                          entry.rank === 1 ? 'border-yellow-500' :
                          entry.rank === 2 ? 'border-gray-400' :
                          entry.rank === 3 ? 'border-amber-600' : 'border-red-500/50'
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <h3 className={`font-bold truncate ${
                          entry.rank === 1 ? 'text-yellow-400' :
                          entry.rank === 2 ? 'text-gray-300' :
                          entry.rank === 3 ? 'text-amber-500' : 'text-white'
                        }`} style={{ fontFamily: 'Orbitron, sans-serif' }}>
                          {entry.display_name}
                          {user?.id === entry.user_id && (
                            <span className="ml-2 text-xs text-red-400">(You)</span>
                          )}
                        </h3>
                        <p className="text-gray-500 text-sm">@{entry.twitch_username}</p>
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="hidden sm:flex items-center gap-4">
                      {/* Place breakdown */}
                      <div className="flex items-center gap-2">
                        {entry.first_place_wins > 0 && (
                          <span className="text-yellow-400 font-bold" title="1st place wins">
                            🥇{entry.first_place_wins}
                          </span>
                        )}
                        {entry.second_place_wins > 0 && (
                          <span className="text-gray-300 font-bold" title="2nd place wins">
                            🥈{entry.second_place_wins}
                          </span>
                        )}
                        {entry.third_place_wins > 0 && (
                          <span className="text-amber-500 font-bold" title="3rd place wins">
                            🥉{entry.third_place_wins}
                          </span>
                        )}
                        {entry.wins_count === 0 && (
                          <span className="text-gray-500 text-sm">No wins</span>
                        )}
                      </div>
                      <div className="text-center">
                        <p className="text-xl font-bold text-red-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                          {entry.participation_count}
                        </p>
                        <p className="text-[10px] text-gray-500 uppercase">Played</p>
                      </div>
                    </div>
                    
                    {/* Score */}
                    <div className="text-right">
                      <p className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-yellow-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                        {entry.score}
                      </p>
                      <p className="text-xs text-gray-500 uppercase">Points</p>
                    </div>
                  </div>
                  
                  {/* Badges Row */}
                  {entry.badges.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-red-900/30 flex flex-wrap gap-2">
                      {entry.badges.map((badge) => (
                        <span 
                          key={badge.id}
                          className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                            badge.color === 'yellow' ? 'bg-yellow-500/20 text-yellow-400' :
                            badge.color === 'gold' ? 'bg-amber-500/20 text-amber-400' :
                            badge.color === 'purple' ? 'bg-purple-500/20 text-purple-400' :
                            badge.color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                            badge.color === 'orange' ? 'bg-orange-500/20 text-orange-400' :
                            badge.color === 'red' ? 'bg-red-500/20 text-red-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}
                        >
                          <BadgeIcon icon={badge.icon} color={badge.color} />
                          {badge.name}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Recent Wins with Challenge Titles */}
                  {entry.recent_wins && entry.recent_wins.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-red-900/30">
                      <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Recent Wins</p>
                      <div className="flex flex-wrap gap-2">
                        {entry.recent_wins.map((win, idx) => (
                          <div 
                            key={idx}
                            className={`px-2 py-1 rounded text-xs font-medium flex flex-col ${
                              win.place === 1 ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                              win.place === 2 ? 'bg-gray-500/20 text-gray-300 border border-gray-500/30' :
                              'bg-amber-500/20 text-amber-500 border border-amber-500/30'
                            }`}
                          >
                            <span className="flex items-center gap-1">
                              {win.place === 1 ? '🥇' : win.place === 2 ? '🥈' : '🥉'}
                              <span className="truncate max-w-[120px] font-semibold">{win.challenge_title}</span>
                            </span>
                            {win.game_name && (
                              <span className="text-purple-400 text-[10px] truncate max-w-[120px]">{win.game_name}</span>
                            )}
                            {win.prize_amount > 0 && (
                              <span className="text-green-400 text-[10px]">${win.prize_amount}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Mobile Stats */}
                  <div className="mt-4 pt-4 border-t border-red-900/30 flex sm:hidden justify-around">
                    <div className="text-center">
                      <p className="text-xl font-bold text-yellow-400">{entry.wins_count}</p>
                      <p className="text-xs text-gray-500">Wins</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-red-400">{entry.participation_count}</p>
                      <p className="text-xs text-gray-500">Played</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-blue-400">{entry.badges.length}</p>
                      <p className="text-xs text-gray-500">Badges</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-red-900/20">
        <div className="max-w-7xl mx-auto text-center text-gray-600">
          <p className="flex items-center justify-center gap-2">
            <Trophy className="w-4 h-4 text-red-500" />
            CesarM Challenges
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Leaderboard;
